import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DeleteTask, PatchTask, PostTask } from '../../redux/taskSlice';
import { ChevronDown20Regular, ArrowSortUp16Regular, Info12Regular } from '@fluentui/react-icons';
import { Tooltip } from '@fluentui/react-components';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import styles from './css/index.module.css';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import PauseTaskConfirmModal from '../CommonComponents/DoubleConfirmationModal/PauseTaskConfirmModal';
import RecalculateScheduleModal from '../CommonComponents/DoubleConfirmationModal/RecalculateScheduleModal';
import AdjustDeadlineModal from '../CommonComponents/DoubleConfirmationModal/AdjustDeadlineModal';
import ResizableColumnHeader from './ResizableColumnHeader';
import HierarchicalTaskRow from './HierarchicalTaskRow';
import SubtaskParentWarningModal from './SubtaskParentWarningModal';
import { DEFAULT_TASK_TABLE_COLUMN_ORDER, TASK_TABLE_COLUMN_LABELS } from '../../utils/taskTableColumns';
import { requestCreateScheduleMutation, requestDeadlineMutation, requestDeleteScheduleMutation, requestScheduleMutation, requestStatusScheduleMutation, shouldConfirmPauseCancelResume, statusNeedsSchedulePreview, statusPatchBody, showScheduleError } from '../../utils/taskScheduleMutation';
import {
    DURATION_SUFFIX_HELP,
    formatDuration,
    isReservedOffTrackTag,
    isSchedulingMode,
    MINUTES_PER_DAY,
    needsSubtaskParentWarning,
    normalizeTaskStatus,
} from '../../utils/taskSchedulingUtils';
import { getChildren, getInheritedTimeline, getTaskIdsInVisibleRange, sortTasksByOrder } from '../../utils/taskHierarchyUtils';

const formatGroupDate = value => {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
};

const computeGroupScheduleSummary = (groupTasks = [], lookupTasks = groupTasks) => {
    let minStart = null;
    let maxEnd = null;
    let totalMinutes = 0;
    let calendarDays = 0;
    let hasMinutes = false;

    (groupTasks || []).forEach(task => {
        if (!task || normalizeTaskStatus(task.status) === 'CANCELLED') return;

        const inherited = getInheritedTimeline(task, lookupTasks || groupTasks);
        const startRaw = task.start_date || inherited?.start_date;
        const endRaw = task.end_date || inherited?.end_date;
        const start = startRaw ? new Date(startRaw) : null;
        const end = endRaw ? new Date(endRaw) : null;
        if (start && !Number.isNaN(start.getTime()) && (!minStart || start < minStart)) minStart = start;
        if (end && !Number.isNaN(end.getTime()) && (!maxEnd || end > maxEnd)) maxEnd = end;

        if (task.duration != null && task.duration !== '' && !Number.isNaN(Number(task.duration))) {
            totalMinutes += Number(task.duration);
            hasMinutes = true;
            return;
        }
        if (start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end > start) {
            calendarDays += Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        }
    });

    const startLabel = formatGroupDate(minStart);
    const endLabel = formatGroupDate(maxEnd);
    let timelineLabel = '';
    if (startLabel && endLabel) timelineLabel = `${startLabel} - ${endLabel}`;
    else timelineLabel = startLabel || endLabel;

    let durationLabel = '';
    if (hasMinutes) durationLabel = formatDuration(totalMinutes + calendarDays * MINUTES_PER_DAY);
    else if (calendarDays > 0) durationLabel = `${calendarDays} day${calendarDays === 1 ? '' : 's'}`;

    return { timelineLabel, durationLabel };
};

const TasksTable = ({
    filteredData,
    setFilteredData,
    projectUsersAll,
    modalVisible,
    setModalVisible,
    selectedRow,
    setSelectedRow,
    isAddingNewTask,
    setIsAddingNewTask,
    newTaskText,
    setNewTaskText,
    selectedProject,
    isColumnVisible,
    onOpenSidebar,
    selectedRows,
    setSelectedRows,
    groupByColumn,
    collapsedGroups,
    onToggleGroup,
    onDragStart,
    onDragMove,
    onDragEnd,
    onDragCancel,
    dropIntent,
    activeDragId,
    customOrder,
    sensors,
    expandedTasks,
    setExpandedTasks,
    addNewTaskFocusTrigger,
    sortColumn,
    sortOrder,
    onRequestSort,
    previewTaskId = null,
    setPreviewTaskId,
}) => {
    const deleteModalRef = React.useRef(null);
    const newTaskInputRef = React.useRef(null);
    const addRowRef = React.useRef(null);
    const dispatch = useDispatch();
    const taskData = useSelector((state) => state.task?.taskData) || [];
    const [editingTaskId, setEditingTaskId] = React.useState(null);
    const [editingTaskText, setEditingTaskText] = React.useState('');
    const tableContainerRef = React.useRef(null);
    const lastSelectedTaskIdRef = React.useRef(null);
    const [highlightAddRow, setHighlightAddRow] = React.useState(false);
    const [groupedNewTaskText, setGroupedNewTaskText] = React.useState({});
    const [pendingSubtaskAdd, setPendingSubtaskAdd] = React.useState(null);
    const [subtaskAddLoading, setSubtaskAddLoading] = React.useState(false);
    const [pendingScheduleChange, setPendingScheduleChange] = React.useState(null);
    const [pendingDeadlineChange, setPendingDeadlineChange] = React.useState(null);
    const [deadlineChangeLoading, setDeadlineChangeLoading] = React.useState(false);
    const [scheduleChangeLoading, setScheduleChangeLoading] = React.useState(false);
    const [pendingStatusChange, setPendingStatusChange] = React.useState(null);
    const [statusChangeLoading, setStatusChangeLoading] = React.useState(false);
    const columnSensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 },
        }),
    );
    // Column width state management
    const [columnWidths, setColumnWidths] = React.useState({
        checkbox: 32,
        // Task extends through former owner+status span (408 + 56 + 104)
        task: 568,
        owner: 56,
        status: 104,
        timeline: 180,
        duration: 140,
        mode: 88,
        deadline: 160,
        priority: 104,
        workstream: 200,
        projectPhase: 200,
        tag: 200,
        lastUpdated: 120,
        addedOn: 120,
        actions: 30,
    });

    // Column order state (excluding checkbox and actions)
    const defaultColumnOrder = [
        'task',
        'owner',
        'status',
        'timeline',
        'duration',
        'mode',
        'deadline',
        'priority',
        'workstream',
        'tag',
        'projectPhase',
        'lastUpdated',
        'addedOn',
    ];
    const [columnOrder, setColumnOrder] = React.useState(() => {
        try {
            const saved = localStorage.getItem('taskTableColumnOrder');
            if (saved) {
                const parsed = JSON.parse(saved);
                // Ensure saved order contains only known columns and include any new ones
                const cleaned = defaultColumnOrder.filter(c => parsed.includes(c));
                const missing = defaultColumnOrder.filter(c => !cleaned.includes(c));
                return [...cleaned, ...missing];
            }
        } catch (e) {}
        return defaultColumnOrder;
    });

    React.useEffect(() => {
        try {
            localStorage.setItem('taskTableColumnOrder', JSON.stringify(columnOrder));
        } catch (e) {}
    }, [columnOrder]);

    // Flatten all tasks (supports grouped + non-grouped data)
    const allTasks = React.useMemo(() => {
        if (filteredData?.isGrouped) {
            return filteredData.groups?.flatMap(group => group.tasks) || [];
        }
        return filteredData?.data || filteredData || [];
    }, [filteredData]);

    // Build a parent -> children map for fast descendant lookups
    const childrenByParentId = React.useMemo(() => {
        const map = new Map();
        allTasks.forEach(t => {
            if (!t?.parent_task) return;
            const parentId = t.parent_task;
            const existing = map.get(parentId);
            if (existing) existing.push(t);
            else map.set(parentId, [t]);
        });
        return map;
    }, [allTasks]);

    React.useEffect(() => {
        if (selectedRows.length === 0) {
            lastSelectedTaskIdRef.current = null;
        }
    }, [selectedRows]);

    // Return all descendant task IDs for a given task ID (all levels)
    const getDescendantTaskIds = React.useCallback(
        taskId => {
            const result = [];
            const visited = new Set();
            const stack = [...(childrenByParentId.get(taskId) || [])];

            while (stack.length > 0) {
                const t = stack.pop();
                if (!t) continue;
                if (visited.has(t.id)) continue;
                visited.add(t.id);

                result.push(t.id);
                const children = childrenByParentId.get(t.id);
                if (children && children.length > 0) {
                    stack.push(...children);
                }
            }

            return result;
        },
        [childrenByParentId],
    );

    const handleColumnResize = (columnKey, newWidth) => {
        setColumnWidths(prev => ({
            ...prev,
            [columnKey]: newWidth,
        }));
    };

    // Sortable header cell
    const SortableHeaderCell = ({ id, width, children }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
        const style = {
            // Remove scale factors to avoid width/height distortion while dragging
            transform: transform ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)` : undefined,
            transition,
            opacity: isDragging ? 0.6 : 1,
            width: `${width}px`,
            minWidth: `${width}px`,
            maxWidth: `${width}px`,
            '--column-width': `${width}px`,
            cursor: isDragging ? 's-resize' : undefined,
        };
        return (
            <th ref={setNodeRef} style={style} className={`${styles.headerRowItem} ${isDragging ? 'isDragging' : ''}`} data-sortable-id={id} {...attributes} {...listeners}>
                <div style={{ width: '100%' }}>{children}</div>
            </th>
        );
    };

    // Focus the bottom add-new-task input and scroll to bottom when activated
    const hasInitializedAddNewTask = React.useRef(false);

    React.useEffect(() => {
        // Avoid auto-scrolling on initial mount so the page doesn't jump down
        if (!hasInitializedAddNewTask.current) {
            hasInitializedAddNewTask.current = true;
            return;
        }

        if (isAddingNewTask && expandedTasks.size === 0 && !groupByColumn) {
            // Scroll the table container to bottom and focus the input
            requestAnimationFrame(() => {
                const container = document.getElementById('style-3-scroll');
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
                if (addRowRef.current) {
                    addRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
                if (newTaskInputRef.current) {
                    newTaskInputRef.current.focus();
                }
            });

            // Trigger 1s highlight animation on the add-new-task row
            setHighlightAddRow(true);
            const timeoutId = setTimeout(() => {
                setHighlightAddRow(false);
            }, 1000);

            return () => {
                clearTimeout(timeoutId);
            };
        }
    }, [isAddingNewTask, expandedTasks.size, addNewTaskFocusTrigger, groupByColumn]);

    // Hierarchy helper functions
    const getRootTasks = tasks => {
        return tasks
            .filter(task => !task.parent_task)
            .sort((a, b) => {
                const orderDiff = (a.order ?? 0) - (b.order ?? 0);
                if (orderDiff !== 0) return orderDiff;
                const aCreated = a.created ? new Date(a.created).getTime() : 0;
                const bCreated = b.created ? new Date(b.created).getTime() : 0;
                if (aCreated !== bCreated) return aCreated - bCreated;
                return (a.id ?? 0) - (b.id ?? 0);
            });
    };

    const handleToggleExpanded = taskId => {
        setExpandedTasks(prev => {
            const newSet = new Set(prev);
            if (newSet.has(taskId)) {
                // If task is already expanded, collapse it and all its children
                const allTasks = filteredData?.isGrouped ? filteredData.groups?.flatMap(group => group.tasks) || [] : filteredData?.data || filteredData || [];

                // Recursively find all children of this task
                const getAllChildren = parentId => {
                    const directChildren = allTasks.filter(t => t.parent_task === parentId);
                    let allChildren = [...directChildren];
                    directChildren.forEach(child => {
                        allChildren = [...allChildren, ...getAllChildren(child.id)];
                    });
                    return allChildren;
                };

                const childrenToCollapse = getAllChildren(taskId);
                childrenToCollapse.forEach(child => newSet.delete(child.id));
                newSet.delete(taskId);
            } else {
                // Add the current task to expanded set
                // Allow multiple rows to be expanded simultaneously
                newSet.add(taskId);
            }
            return newSet;
        });
    };

    const createChildTask = async (parentTaskId, childTaskText, { recalculateDownstream = false } = {}) => {
        await dispatch(
            PostTask({
                task: childTaskText,
                project: selectedProject?.id,
                parent_task: parentTaskId,
                status: 'NOT_STARTED',
                recalculateDownstream,
            }),
        );
    };

    const submitCreateTask = async (payload, { recalculateDownstream = false } = {}) => {
        await dispatch(PostTask({ ...payload, recalculateDownstream }));
    };

    const handleAddChildTask = async (parentTaskId, childTaskText) => {
        const trimmed = childTaskText.trim();
        if (!trimmed) return;

        const parentTask =
            taskData.find(t => t.id === parentTaskId) ||
            allTasks.find(t => t.id === parentTaskId);
        const tasksForCheck = taskData.length ? taskData : allTasks;
        if (isSchedulingMode(selectedProject) && needsSubtaskParentWarning(parentTask, tasksForCheck)) {
            setPendingSubtaskAdd({ parentTaskId, childTaskText: trimmed });
            return;
        }

        try {
            await requestCreateScheduleMutation({
                payload: {
                    task: trimmed,
                    project: selectedProject?.id,
                    parent_task: parentTaskId,
                    status: 'NOT_STARTED',
                },
                setPending: setPendingScheduleChange,
                applyCreate: submitCreateTask,
                setPreviewTaskId,
            });
        } catch (error) {
            console.error('Error creating child task:', error);
        }
    };

    const handleSubtaskParentWarningClose = () => {
        if (subtaskAddLoading) return;
        setPendingSubtaskAdd(null);
    };

    const handleSubtaskParentWarningConfirm = async () => {
        if (!pendingSubtaskAdd || subtaskAddLoading) return;

        setSubtaskAddLoading(true);
        try {
            await createChildTask(pendingSubtaskAdd.parentTaskId, pendingSubtaskAdd.childTaskText);
            setPendingSubtaskAdd(null);
        } catch (error) {
            console.error('Error creating child task:', error);
        } finally {
            setSubtaskAddLoading(false);
        }
    };
    const applyStatusPatch = async (task, newStatus, { cascadeStatus = false, updateOnly, resumeDownstream = false } = {}) => {
        const updateBody = statusPatchBody(task, newStatus);
        if (cascadeStatus) updateBody.cascade_status = true;
        if (resumeDownstream) updateBody.resume_downstream = true;
        if (updateOnly !== undefined) updateBody.update_only = updateOnly;
        await dispatch(
            PatchTask({
                body: updateBody,
                id: task.id,
                suppressToast: true,
            }),
        );
    };

    const handleStatusChange = async (task, newStatus) => {
        const normalized = normalizeTaskStatus(newStatus);
        const prev = normalizeTaskStatus(task.status);

        const pauseCancelResume = shouldConfirmPauseCancelResume(normalized, prev);
        if (pauseCancelResume) {
            setPendingStatusChange({
                task,
                newStatus: normalized,
                action: pauseCancelResume.action,
            });
            return;
        }

        if (statusNeedsSchedulePreview(normalized, prev)) {
            try {
                await requestStatusScheduleMutation({
                    task,
                    newStatus: normalized,
                    setPending: setPendingScheduleChange,
                    applyPatch: applySchedulePatch,
                    setPreviewTaskId,
                });
            } catch (error) {
                console.error('Error updating task status:', error);
            }
            return;
        }

        try {
            await applyStatusPatch(task, normalized, { updateOnly: true });
        } catch (error) {
            console.error('Error updating task status:', error);
        }
    };

    const handleStatusThisOnly = async () => {
        if (!pendingStatusChange || statusChangeLoading) return;
        setStatusChangeLoading(true);
        try {
            const { task, newStatus, action } = pendingStatusChange;
            // A-10: resume this-only keeps subsequent timelines; pause/cancel this-only pulls subsequent forward
            await applyStatusPatch(task, newStatus, {
                cascadeStatus: false,
                updateOnly: action === 'resume',
            });
            setPendingStatusChange(null);
        } catch (error) {
            console.error('Error updating task status:', error);
        } finally {
            setStatusChangeLoading(false);
        }
    };

    const handleStatusWithFollowing = async () => {
        if (!pendingStatusChange || statusChangeLoading) return;
        setStatusChangeLoading(true);
        try {
            const { task, newStatus, action } = pendingStatusChange;
            if (action === 'resume') {
                // A-10: resume + recalculate downstream (Paused below → Not Started)
                await applyStatusPatch(task, newStatus, {
                    cascadeStatus: true,
                    resumeDownstream: true,
                    updateOnly: false,
                });
            } else {
                // A-08/A-09: pause/cancel this + all subsequent
                await applyStatusPatch(task, newStatus, { cascadeStatus: true });
            }
            setPendingStatusChange(null);
        } catch (error) {
            console.error('Error updating task status:', error);
        } finally {
            setStatusChangeLoading(false);
        }
    };

    const applySchedulePatch = async (
        task,
        payload,
        { updateOnly, adjustDownstreamDeadlines, adjustTimelineToDeadline } = {},
    ) => {
        const body = { ...payload, update_only: updateOnly };
        if (adjustDownstreamDeadlines) body.adjust_downstream_deadlines = true;
        if (adjustTimelineToDeadline) body.adjust_timeline_to_deadline = true;
        await dispatch(
            PatchTask({
                body,
                id: task.id,
                suppressToast: true,
            }),
        ).unwrap();
    };

    const handleRequestTimelineChange = (task, payload) => {
        requestScheduleMutation({
            task,
            payload,
            fieldLabel: 'Timeline',
            defaultUpdateOnly: true,
            setPending: setPendingScheduleChange,
            applyPatch: applySchedulePatch,
            setPreviewTaskId,
        }).catch(error => console.error('Error updating timeline:', error));
    };

    const handleRequestDurationChange = (task, duration) => {
        requestScheduleMutation({
            task,
            payload: { duration },
            fieldLabel: 'Duration',
            defaultUpdateOnly: true,
            setPending: setPendingScheduleChange,
            applyPatch: applySchedulePatch,
            setPreviewTaskId,
        }).catch(error => console.error('Error updating duration:', error));
    };

    const handleRequestModeChange = (task, mode) => {
        requestScheduleMutation({
            task,
            payload: { mode },
            fieldLabel: 'Mode',
            defaultUpdateOnly: true,
            setPending: setPendingScheduleChange,
            applyPatch: applySchedulePatch,
            setPreviewTaskId,
        }).catch(error => console.error('Error updating mode:', error));
    };

    const handleRequestDeadlineChange = (task, { deadline }) => {
        requestDeadlineMutation({
            task,
            deadline,
            setPendingDeadline: setPendingDeadlineChange,
            setPendingSchedule: setPendingScheduleChange,
            applyPatch: applySchedulePatch,
            setPreviewTaskId,
        }).catch(error => console.error('Error updating deadline:', error));
    };

    const applyScheduleChange = async ({ updateOnly, adjustDownstreamDeadlines = false } = {}) => {
        if (!pendingScheduleChange || scheduleChangeLoading) return;
        setScheduleChangeLoading(true);
        try {
            if (pendingScheduleChange.createTask) {
                await dispatch(
                    PostTask({
                        ...pendingScheduleChange.payload,
                        recalculateDownstream: !updateOnly,
                    }),
                );
            } else if (pendingScheduleChange.deleteTask) {
                await dispatch(
                    DeleteTask({
                        id: pendingScheduleChange.task.id,
                        recalculateDownstream: !updateOnly,
                    }),
                );
                setSelectedRow(null);
            } else {
                await applySchedulePatch(pendingScheduleChange.task, pendingScheduleChange.payload, {
                    updateOnly,
                    adjustDownstreamDeadlines,
                    adjustTimelineToDeadline: pendingScheduleChange.previewFlags?.adjustTimelineToDeadline,
                });
            }
            setPendingScheduleChange(null);
        } catch (error) {
            showScheduleError(error);
            console.error('Error updating schedule:', error);
        } finally {
            setScheduleChangeLoading(false);
        }
    };

    const handlePriorityChange = async (task, newPriority) => {
        try {
            await dispatch(
                PatchTask({
                    body: { priority: newPriority },
                    id: task.id,
                    suppressToast: true,
                }),
            );
        } catch (error) {
            console.error('Error updating task priority:', error);
        }
    };

    const handleTagSelect = async (task, tag) => {
        try {
            // Toggle tag membership in the task's tags array.
            const tagName = tag?.name || '';
            if (isReservedOffTrackTag(tagName)) return;

            const existingTags = Array.isArray(task.tags)
                ? task.tags
                : task?.tag_repr
                ? [task.tag_repr]
                : [];
            const reserved = existingTags.filter(t => isReservedOffTrackTag(t));
            const userTags = existingTags.filter(t => !isReservedOffTrackTag(t));

            let nextUserTags;
            if (!tagName) {
                nextUserTags = [];
            } else if (userTags.includes(tagName)) {
                nextUserTags = userTags.filter(t => t !== tagName);
            } else {
                nextUserTags = [...userTags, tagName];
            }

            await dispatch(
                PatchTask({
                    body: { tags: [...nextUserTags, ...reserved] },
                    id: task.id,
                    suppressToast: true,
                }),
            );
        } catch (error) {
            console.error('Error updating task tag:', error);
        }
    };

    const handleWorkstreamSelect = async (task, workstream) => {
        try {
            const newWorkstreamName = workstream?.name || '';
            await dispatch(
                PatchTask({
                    body: { workstream_repr: newWorkstreamName },
                    id: task.id,
                    suppressToast: true,
                }),
            );
        } catch (error) {
            console.error('Error updating task workstream:', error);
        }
    };

    const handleUserAssignment = async (task, user) => {
        // This function is called after the API call is successful
        // The actual API call is handled in the UserAssignmentMenu component
        console.log('User assigned:', user);
    };

    const handleDeadlineSelect = async (task, deadlineData) => {
        handleRequestDeadlineChange(task, deadlineData);
    };

    const applyDeadlineChange = async ({ adjustTimelineToDeadline = false } = {}) => {
        if (!pendingDeadlineChange || deadlineChangeLoading) return;
        setDeadlineChangeLoading(true);
        try {
            const { task, payload } = pendingDeadlineChange;
            if (adjustTimelineToDeadline) {
                const preview = await import('../../utils/taskSchedulingUtils').then(m =>
                    m.fetchSchedulePreview(task.id, payload, { adjustTimelineToDeadline: true }),
                );
                if (preview.would_affect_others) {
                    setPendingDeadlineChange(null);
                    setPendingScheduleChange({
                        task,
                        payload,
                        fieldLabel: 'Timeline',
                        affectedCount: preview.affected_count,
                        previewFlags: { adjustTimelineToDeadline: true },
                    });
                    return;
                }
                await applySchedulePatch(task, payload, {
                    updateOnly: true,
                    adjustTimelineToDeadline: true,
                });
            } else {
                await applySchedulePatch(task, payload, { updateOnly: true });
            }
            setPendingDeadlineChange(null);
        } catch (error) {
            console.error('Error updating deadline:', error);
        } finally {
            setDeadlineChangeLoading(false);
        }
    };

    const handleProjectPhaseChange = async (task, phase) => {
        try {
            await dispatch(
                PatchTask({
                    body: { project_phase: phase?.name || null },
                    id: task.id,
                    suppressToast: true,
                }),
            );
        } catch (error) {
            console.error('Error updating project phase:', error);
        }
    };

    const parseGroupDeadlineToIso = groupName => {
        if (!groupName || groupName === 'No Deadline') return '';
        const parts = groupName.split('/');
        if (parts.length !== 3) return '';

        const [dd, mm, yy] = parts.map(part => part.trim());
        const day = Number(dd);
        const month = Number(mm);
        const shortYear = Number(yy);

        if (!day || !month || Number.isNaN(shortYear)) return '';

        const fullYear = shortYear < 100 ? 2000 + shortYear : shortYear;
        const parsedDate = new Date(fullYear, month - 1, day);
        if (Number.isNaN(parsedDate.getTime())) return '';

        const normalizedDay = String(day).padStart(2, '0');
        const normalizedMonth = String(month).padStart(2, '0');
        return `${fullYear}-${normalizedMonth}-${normalizedDay}`;
    };

    const getGroupInferredFields = groupName => {
        if (!filteredData?.isGrouped || !groupByColumn || !groupName) return {};

        switch (groupByColumn) {
            case 'status': {
                const statusMap = {
                    'Not Started': 'NOT_STARTED',
                    'Working on it': 'WORKING_ON_IT',
                    Completed: 'COMPLETED',
                    Blocked: 'BLOCKED',
                    Stuck: 'BLOCKED',
                    Paused: 'PAUSED',
                    Cancelled: 'CANCELLED',
                };
                const status = statusMap[groupName];
                return status ? { status } : {};
            }
            case 'priority': {
                const priorityMap = {
                    High: 'HIGH',
                    Medium: 'MEDIUM',
                    Low: 'LOW',
                };
                const priority = priorityMap[groupName];
                return priority ? { priority } : {};
            }
            case 'owner': {
                if (groupName === 'Unassigned') return {};
                const matchedUser = projectUsersAll?.find(user => user?.value?.full_name === groupName);
                const assignedTo = matchedUser?.value?.user_id;
                return assignedTo ? { assigned_to: assignedTo } : {};
            }
            case 'workstream':
                return groupName === 'No Workstream' ? {} : { workstream_repr: groupName };
            case 'project_phase':
                return groupName === 'No Project Phase' ? {} : { project_phase: groupName };
            case 'deadline': {
                const deadline = parseGroupDeadlineToIso(groupName);
                return deadline ? { deadline } : {};
            }
            default:
                return {};
        }
    };

    const scrollTableToBottom = () => {
        setTimeout(() => {
            if (tableContainerRef.current) {
                tableContainerRef.current.scrollTo({
                    top: tableContainerRef.current.scrollHeight,
                    behavior: 'smooth',
                });
            } else {
                const container = document.getElementById('style-3-scroll');
                if (container) {
                    container.scrollTo({
                        top: container.scrollHeight,
                        behavior: 'smooth',
                    });
                }
            }
        }, 100);
    };

    const createTaskFromInput = async groupName => {
        const useGroupedInput = Boolean(groupName && filteredData?.isGrouped);
        const activeTaskText = useGroupedInput ? groupedNewTaskText[groupName] || '' : newTaskText;
        const taskText = activeTaskText.trim();
        if (!taskText) return;

        const payload = {
            task: taskText,
            project: selectedProject?.id,
            status: 'NOT_STARTED',
            ...getGroupInferredFields(groupName),
        };

        await requestCreateScheduleMutation({
            payload,
            setPending: setPendingScheduleChange,
            applyCreate: submitCreateTask,
            setPreviewTaskId,
        });
        if (useGroupedInput) {
            setGroupedNewTaskText(prev => ({ ...prev, [groupName]: '' }));
        } else {
            setNewTaskText('');
        }
        scrollTableToBottom();
    };

    const handleTaskTextClick = (e, task) => {
        e.stopPropagation();
        setEditingTaskId(task.id);
        // Convert HTML to plain text for editing
        const plainText = task.task ? task.task.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ') : '';
        setEditingTaskText(plainText);
    };

    const handleTaskTextSave = async (task, textOverride) => {
        const nextText = textOverride !== undefined ? textOverride : editingTaskText;
        const originalText = task.task ? task.task.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ') : '';
        if (nextText.trim() !== originalText.trim()) {
            try {
                await dispatch(
                    PatchTask({
                        body: { task: nextText },
                        id: task.id,
                        suppressToast: true,
                    }),
                );
            } catch (error) {
                console.error('Error updating task text:', error);
            }
        }
        setEditingTaskId(null);
        setEditingTaskText('');
    };

    const handleTaskTextCancel = () => {
        setEditingTaskId(null);
        setEditingTaskText('');
    };

    const handleTaskTextKeyPress = (e, task) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
            handleTaskTextSave(task, e.target.value);
        } else if (e.key === 'Escape') {
            handleTaskTextCancel();
        }
    };

    const getUserInfo = createdBy => {
        const user = projectUsersAll?.find(user => user?.value?.user_id === createdBy);
        return user?.value || null;
    };

    const handleStarClick = async (e, task) => {
        e.stopPropagation();

        if (!task.starred) {
            // Only one key task per project — unstar from full project data (not filtered view)
            const otherStarred = (taskData || []).filter(t => t.id !== task.id && t.starred);
            for (const other of otherStarred) {
                await dispatch(PatchTask({ body: { starred: false }, id: other.id, suppressToast: true }));
            }
            await dispatch(PatchTask({ body: { starred: true }, id: task.id, suppressToast: true }));
        } else {
            await dispatch(PatchTask({ body: { starred: false }, id: task.id, suppressToast: true }));
        }
    };

    const handleEdit = task => {
        setSelectedRow(task);
        setModalVisible(true);
    };

    const handleDelete = task => {
        setSelectedRow(task);
        deleteModalRef?.current?.click();
    };

    const getVisibleTaskIds = () => {
        const collectVisibleIds = taskList => {
            const ids = [];
            const walk = task => {
                ids.push(task.id);
                if (!expandedTasks?.has(task.id)) return;
                sortTasksByOrder(getChildren(task.id, allTasks), customOrder).forEach(walk);
            };
            getRootTasks(taskList).forEach(walk);
            return ids;
        };

        if (filteredData?.isGrouped) {
            return (filteredData.groups || []).flatMap(group => {
                if (collapsedGroups?.has(group.groupName)) return [];
                return collectVisibleIds(group.tasks || []);
            });
        }

        return collectVisibleIds(filteredData?.data || filteredData || []);
    };

    const handleSelectRow = (taskId, event) => {
        const isShiftClick = Boolean(event?.shiftKey || event?.nativeEvent?.shiftKey);

        if (isShiftClick && lastSelectedTaskIdRef.current != null) {
            const rangeIds = getTaskIdsInVisibleRange(getVisibleTaskIds(), lastSelectedTaskIdRef.current, taskId);
            setSelectedRows(prev => {
                const next = new Set(prev);
                rangeIds.forEach(id => {
                    next.add(id);
                    getDescendantTaskIds(id).forEach(descId => next.add(descId));
                });
                return Array.from(next);
            });
            return;
        }

        lastSelectedTaskIdRef.current = taskId;
        const descendantIds = getDescendantTaskIds(taskId);
        setSelectedRows(prev => {
            const next = new Set(prev);
            if (isShiftClick) {
                next.add(taskId);
                descendantIds.forEach(id => next.add(id));
                return Array.from(next);
            }

            const isSelected = next.has(taskId);
            if (isSelected) {
                next.delete(taskId);
                descendantIds.forEach(id => next.delete(id));
            } else {
                next.add(taskId);
                descendantIds.forEach(id => next.add(id));
            }

            return Array.from(next);
        });
    };

    const handleSelectAll = (groupTasks = null) => {
        // If groupTasks is provided, we're in grouped mode and should only select tasks from this group
        const tasksToSelect = groupTasks || (filteredData?.isGrouped ? filteredData.groups?.flatMap(group => group.tasks) || [] : filteredData?.data || filteredData || []);

        if (groupTasks) {
            // Grouped mode: only select/deselect tasks from this specific group
            const groupTaskIds = groupTasks.map(task => task.id);
            const allGroupTasksSelected = groupTaskIds.every(id => selectedRows.includes(id));

            if (allGroupTasksSelected) {
                // Deselect all tasks from this group
                setSelectedRows(prev => prev.filter(id => !groupTaskIds.includes(id)));
            } else {
                // Select all tasks from this group (keep other groups' selections)
                setSelectedRows(prev => {
                    const otherGroupSelections = prev.filter(id => !groupTaskIds.includes(id));
                    return [...otherGroupSelections, ...groupTaskIds];
                });
            }
        } else {
            // Non-grouped mode: select/deselect all tasks
            if (selectedRows.length === tasksToSelect?.length) {
                setSelectedRows([]);
            } else {
                setSelectedRows(tasksToSelect?.map(task => task.id) || []);
            }
        }
    };

    const getStatusInfo = status => {
        const notStarted = { bg: '#FFFFFF', color: '#989BAD', text: 'Not Started' };
        const statusMap = {
            // Backend status values (what actually comes from API)
            NOT_STARTED: notStarted,
            WORKING_ON_IT: { bg: 'var(--RAG-Amber)', color: 'white', text: 'Working on it' },
            COMPLETED: { bg: 'var(--RAG-Green)', color: 'white', text: 'Completed' },
            STUCK: { bg: 'var(--RAG-Red)', color: 'white', text: 'Blocked' },
            BLOCKED: { bg: 'var(--RAG-Red)', color: 'white', text: 'Blocked' },
            PAUSED: { bg: '#D5D8E0', color: '#5A5F7A', text: 'Paused', border: '1px solid #C8CCD6' },
            CANCELLED: { bg: '#989BAD', color: 'white', text: 'Cancelled' },
            NOT_APPLICABLE: notStarted,
            // Legacy status support (for backward compatibility)
            PENDING: { bg: 'var(--RAG-Amber)', color: 'white', text: 'Working on it' },
            COMPLETE: { bg: 'var(--RAG-Green)', color: 'white', text: 'Completed' },
            // Fallback for unknown statuses
            'Not started': notStarted,
            'Working on it': { bg: 'var(--RAG-Amber)', color: 'white', text: 'Working on it' },
            Completed: { bg: 'var(--RAG-Green)', color: 'white', text: 'Completed' },
            Stuck: { bg: 'var(--RAG-Red)', color: 'white', text: 'Blocked' },
            Blocked: { bg: 'var(--RAG-Red)', color: 'white', text: 'Blocked' },
            Paused: { bg: '#D5D8E0', color: '#5A5F7A', text: 'Paused', border: '1px solid #C8CCD6' },
            Cancelled: { bg: '#989BAD', color: 'white', text: 'Cancelled' },
            'Not Applicable': notStarted,
        };

        // If status exists in map, use it; otherwise return empty status
        if (statusMap[status]) {
            return statusMap[status];
        }

        // Default missing statuses to Not Started.
        return notStarted;
    };

    const formatLastUpdated = lastUpdated => {
        if (!lastUpdated) return '';

        try {
            const date = new Date(lastUpdated);
            const now = new Date();
            const diffInMs = now - date;
            const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
            const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

            if (diffInMinutes < 1) {
                return 'just now';
            } else if (diffInMinutes < 60) {
                return `${diffInMinutes} min ago`;
            } else if (diffInHours < 24) {
                return `${diffInHours} hrs ago`;
            } else {
                // Format as DD/MM/YYYY
                return date.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                });
            }
        } catch (error) {
            console.error('Error formatting last updated date:', error);
            return '';
        }
    };

    const formatDeadline = (deadline) => {
        if (!deadline) return '';

        try {
            const deadlineDate = new Date(deadline);
            return deadlineDate.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric', // DD/MM/YYYY
            });
        } catch (error) {
            console.error('Error formatting deadline:', error);
            return '';
        }
    };

    const getPriorityInfo = priority => {
        const nonePriority = { bg: '#FFFFFF', color: '#989BAD', text: 'None' };
        const priorityMap = {
            HIGH: { bg: '#2B35AF', color: 'white', text: 'High' },
            MEDIUM: { bg: '#4361EE', color: 'white', text: 'Medium' },
            LOW: { bg: '#4895EF', color: 'white', text: 'Low' },
            '': nonePriority,
            NONE: nonePriority,
            None: nonePriority,
        };

        // If priority exists in map, use it; otherwise return empty priority
        if (priorityMap[priority]) {
            return priorityMap[priority];
        }

        // Return empty priority for undefined/null values
        return nonePriority;
    };

    // Helper function to render a single table
    const renderTable = (tasks, groupName = null) => {
        const renderSortLabel = (colKey, label) => {
            const isActive = sortColumn === colKey;
            const isDesc = sortOrder === 'desc';
            return (
                <div
                    role="button"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none' }}
                    onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        onRequestSort && onRequestSort(colKey);
                    }}>
                    <span>{label}</span>
                    <ArrowSortUp16Regular
                        color={isActive ? 'var(--Brand-Secondary)' : 'rgba(130, 154, 177, 0.8)'}
                        style={{
                            transform: isActive && isDesc ? 'rotate(180deg)' : 'none',
                            transition: 'transform 120ms ease',
                        }}
                    />
                </div>
            );
        };

        const lookupTasks = allTasks?.length ? allTasks : taskData;
        const groupSummary = groupName ? computeGroupScheduleSummary(tasks, lookupTasks) : { timelineLabel: '', durationLabel: '' };

        return (
            <div className={styles.tableContainer} ref={tableContainerRef} id="style-3-scroll" style={filteredData?.isGrouped ? { height: 'auto' } : undefined}>
                {groupName && (
                    <div className="mt-3 mb-2">
                        <span className={styles.todoText} onClick={() => onToggleGroup(groupName)} style={{ cursor: 'pointer' }}>
                            {!collapsedGroups.has(groupName) ? (
                                <ChevronDown20Regular className="me-1" />
                            ) : (
                                <ChevronDown20Regular className="me-1" style={{ transform: 'rotate(-90deg)' }} />
                            )}
                            {groupName} ({tasks.length} {tasks.length === 1 ? 'task' : 'tasks'})
                            {groupSummary.timelineLabel ? ` · ${groupSummary.timelineLabel}` : ''}
                            {groupSummary.durationLabel ? ` · ${groupSummary.durationLabel}` : ''}
                        </span>
                    </div>
                )}
                {!(groupName && collapsedGroups.has(groupName)) && (
                    <table className="w-100">
                        <thead>
                            <DndContext
                                sensors={columnSensors}
                                collisionDetection={closestCenter}
                                onDragStart={() => {
                                    // Show down arrow cursor while dragging columns
                                    document.body.style.cursor = 's-resize';
                                }}
                                onDragEnd={event => {
                                    const { active, over } = event;
                                    if (!over || active.id === over.id) return;
                                    const oldIndex = columnOrder.indexOf(String(active.id));
                                    const newIndex = columnOrder.indexOf(String(over.id));
                                    if (oldIndex === -1 || newIndex === -1) return;
                                    const updated = [...columnOrder];
                                    const [moved] = updated.splice(oldIndex, 1);
                                    updated.splice(newIndex, 0, moved);
                                    setColumnOrder(updated);
                                    // Restore cursor after drag
                                    document.body.style.cursor = '';
                                }}
                                onDragCancel={() => {
                                    // Restore cursor if drag is cancelled
                                    document.body.style.cursor = '';
                                }}>
                                <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
                                    <tr
                                        className={styles.tableHeader}
                                        onMouseEnter={e => {
                                            const checkbox = e.currentTarget.querySelector('input[type="checkbox"]');
                                            if (checkbox) {
                                                checkbox.style.opacity = '1';
                                            }
                                        }}
                                        onMouseLeave={e => {
                                            const checkbox = e.currentTarget.querySelector('input[type="checkbox"]');
                                            if (checkbox) {
                                                checkbox.style.opacity = '0.5';
                                            }
                                        }}>
                                        <th className={styles.headerRowItem} style={{ width: columnWidths.checkbox }}>
                                            <input
                                                className={styles.checkboxTable}
                                                type="checkbox"
                                                checked={
                                                    groupName
                                                        ? tasks.every(task => selectedRows.includes(task.id)) && tasks.length > 0
                                                        : selectedRows.length === allTasks?.length && allTasks?.length > 0
                                                }
                                                onChange={() => handleSelectAll(groupName ? tasks : null)}
                                                style={{ opacity: 0.5 }}
                                            />
                                        </th>

                                        {columnOrder.map(col => {
                                            if (!isColumnVisible(col)) return null;
                                            if (col === 'mode' && !isSchedulingMode(selectedProject)) return null;
                                            if (col === 'task') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.task}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths.task}
                                                            minWidth={408}
                                                            maxWidth={800}
                                                            onResize={newWidth => handleColumnResize('task', newWidth)}>
                                                            Task
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            if (col === 'workstream') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.workstream}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths.workstream}
                                                            minWidth={120}
                                                            maxWidth={500}
                                                            onResize={newWidth => handleColumnResize('workstream', newWidth)}>
                                                            Workstream
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            if (col === 'tag') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.tag}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths.tag}
                                                            minWidth={100}
                                                            maxWidth={400}
                                                            onResize={newWidth => handleColumnResize('tag', newWidth)}>
                                                            Tag
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            if (col === 'projectPhase') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.projectPhase}>
                                                        <ResizableColumnHeader
                                                            width={columnWidths.projectPhase}
                                                            minWidth={140}
                                                            maxWidth={360}
                                                            onResize={newWidth => handleColumnResize('projectPhase', newWidth)}>
                                                            Project Phase
                                                        </ResizableColumnHeader>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            if (col === 'duration') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.duration}>
                                                        <span className={styles.durationHeaderLabel}>
                                                            Duration
                                                            <Tooltip
                                                                relationship="description"
                                                                positioning="above"
                                                                withArrow
                                                                content={DURATION_SUFFIX_HELP}>
                                                                <span
                                                                    className={styles.durationHeaderInfo}
                                                                    role="img"
                                                                    tabIndex={0}
                                                                    aria-label="Duration format"
                                                                    onPointerDown={e => e.stopPropagation()}
                                                                    onMouseDown={e => e.stopPropagation()}
                                                                    onClick={e => e.stopPropagation()}>
                                                                    <Info12Regular />
                                                                </span>
                                                            </Tooltip>
                                                        </span>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            if (col === 'mode') {
                                                return (
                                                    <SortableHeaderCell key={col} id={col} width={columnWidths.mode}>
                                                        <Tooltip
                                                            relationship="description"
                                                            positioning="above"
                                                            withArrow
                                                            content="Defines how this task is scheduled relative to the previous eligible task.">
                                                            <span>Mode</span>
                                                        </Tooltip>
                                                    </SortableHeaderCell>
                                                );
                                            }
                                            const labelMap = {
                                                owner: 'Owner',
                                                status: 'Status',
                                                timeline: 'Timeline',
                                                deadline: 'Deadline',
                                                mode: 'Mode',
                                                priority: 'Priority',
                                                projectPhase: 'Project Phase',
                                                lastUpdated: 'Last Updated',
                                                addedOn: 'Added On',
                                            };
                                            const headerContent =
                                                col === 'lastUpdated'
                                                    ? renderSortLabel('lastUpdated', labelMap.lastUpdated)
                                                    : col === 'addedOn'
                                                    ? renderSortLabel('addedOn', labelMap.addedOn)
                                                    : labelMap[col];
                                            return (
                                                <SortableHeaderCell key={col} id={col} width={columnWidths[col]}>
                                                    {headerContent}
                                                </SortableHeaderCell>
                                            );
                                        })}

                                        <th className={styles.headerRowItem} style={{ width: columnWidths.actions }}></th>
                                    </tr>
                                </SortableContext>
                            </DndContext>
                        </thead>

                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={onDragStart}
                            onDragMove={onDragMove}
                            onDragEnd={onDragEnd}
                            onDragCancel={onDragCancel}>
                            <tbody>
                                {getRootTasks(tasks)?.map((task, index) => (
                                    <HierarchicalTaskRow
                                        key={task.id}
                                        task={task}
                                        index={index}
                                        editingTaskId={editingTaskId}
                                        setEditingTaskId={setEditingTaskId}
                                        editingTaskText={editingTaskText}
                                        setEditingTaskText={setEditingTaskText}
                                        handleTaskTextClick={handleTaskTextClick}
                                        handleTaskTextSave={handleTaskTextSave}
                                        handleTaskTextCancel={handleTaskTextCancel}
                                        handleTaskTextKeyPress={handleTaskTextKeyPress}
                                        handleStarClick={handleStarClick}
                                        handleUserAssignment={handleUserAssignment}
                                        handleDeadlineSelect={handleDeadlineSelect}
                                        handleStatusChange={handleStatusChange}
                                        handlePriorityChange={handlePriorityChange}
                                        handleTagSelect={handleTagSelect}
                                        handleWorkstreamSelect={handleWorkstreamSelect}
                                        handleEdit={handleEdit}
                                        handleDelete={handleDelete}
                                        handleSelectRow={handleSelectRow}
                                        selectedRows={selectedRows}
                                        isColumnVisible={isColumnVisible}
                                        columnWidths={columnWidths}
                                        columnOrder={columnOrder}
                                        projectUsersAll={projectUsersAll}
                                        filteredData={filteredData}
                                        onOpenSidebar={onOpenSidebar}
                                        getUserInfo={getUserInfo}
                                        getStatusInfo={getStatusInfo}
                                        getPriorityInfo={getPriorityInfo}
                                        formatLastUpdated={formatLastUpdated}
                                        formatDeadline={formatDeadline}
                                        level={0}
                                        isExpanded={expandedTasks.has(task.id)}
                                        onToggleExpanded={handleToggleExpanded}
                                        onAddChildTask={handleAddChildTask}
                                        expandedTasks={expandedTasks}
                                        handleProjectPhaseChange={handleProjectPhaseChange}
                                        dropIntent={dropIntent}
                                        activeDragId={activeDragId}
                                        customOrder={customOrder}
                                        allTasks={allTasks}
                                        scheduleTasks={taskData}
                                        onRequestDurationChange={handleRequestDurationChange}
                                        onRequestTimelineChange={handleRequestTimelineChange}
                                        onRequestModeChange={handleRequestModeChange}
                                        onRequestDeadlineChange={handleRequestDeadlineChange}
                                        previewTaskId={previewTaskId}
                                    />
                                ))}
                                    {isAddingNewTask &&
                                        expandedTasks.size === 0 &&
                                        (() => {
                                            // Calculate total number of columns: checkbox + visible columns + actions
                                            const visibleColumnsCount = columnOrder.filter(col => isColumnVisible(col)).length;
                                            const totalColumns = 1 + visibleColumnsCount + 1; // checkbox + visible columns + actions
                                            const useGroupedInput = Boolean(groupName && filteredData?.isGrouped);
                                            const activeNewTaskText = useGroupedInput ? groupedNewTaskText[groupName] || '' : newTaskText;

                                            return (
                                                <tr
                                                    ref={addRowRef}
                                                    className={`${styles.tableRow} ${highlightAddRow && !groupByColumn ? styles.highlightAddRow : ''}`}
                                                    style={{ backgroundColor: '#F3FDFE', position: 'sticky', bottom: 0, zIndex: 1 }}>
                                                    {/* Single cell with colspan spanning all columns */}
                                                    <td className={styles.tableRowItem} colSpan={totalColumns}>
                                                        <div className="d-flex align-items-center justify-content-between">
                                                            <div className="flex-fill">
                                                                <input
                                                                    ref={newTaskInputRef}
                                                                    type="text"
                                                                    className={'form-control form-control-sm ' + styles.taskTextField}
                                                                    value={activeNewTaskText}
                                                                    style={{ border: '1px solid #dddee4', marginLeft: '36px', maxWidth: '400px' }}
                                                                    onChange={e => {
                                                                        if (useGroupedInput) {
                                                                            setGroupedNewTaskText(prev => ({
                                                                                ...prev,
                                                                                [groupName]: e.target.value,
                                                                            }));
                                                                        } else {
                                                                            setNewTaskText(e.target.value);
                                                                        }
                                                                    }}
                                                                    onKeyDown={async e => {
                                                                        if (e.key === 'Enter') {
                                                                            e.preventDefault();
                                                                            await createTaskFromInput(groupName);
                                                                        } else if (e.key === 'Escape') {
                                                                            if (useGroupedInput) {
                                                                                setGroupedNewTaskText(prev => ({ ...prev, [groupName]: '' }));
                                                                            } else {
                                                                                setNewTaskText('');
                                                                            }
                                                                        }
                                                                    }}
                                                                    onBlur={async () => {
                                                                        if (!activeNewTaskText.trim()) {
                                                                            if (useGroupedInput) {
                                                                                setGroupedNewTaskText(prev => ({ ...prev, [groupName]: '' }));
                                                                            } else {
                                                                                setNewTaskText('');
                                                                            }
                                                                            return;
                                                                        }
                                                                        await createTaskFromInput(groupName);
                                                                    }}
                                                                    placeholder="Add new task"
                                                                />
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })()}
                                </tbody>
                        </DndContext>
                    </table>
                )}
            </div>
        );
    };

    return (
        <>
            {filteredData?.isGrouped
                ? filteredData.groups?.length
                    ? filteredData.groups.map((group, groupIndex) => (
                          <div key={groupIndex} className={groupIndex > 0 ? 'mt-4' : ''}>
                              {renderTable(group.tasks, group.groupName)}
                          </div>
                      ))
                    : renderTable([])
                : renderTable(filteredData?.data || filteredData || [])}

            {/* Delete Confirmation Modal */}
            <DoubleConfirmationModal
                message="Deleting a Task will permanently remove it and all associated comments from the Project."
                handleAction={() => {
                    if (!selectedRow) return;
                    requestDeleteScheduleMutation({
                        task: selectedRow,
                        setPending: pending =>
                            setPendingScheduleChange({
                                ...pending,
                                deleteTask: true,
                            }),
                        applyDelete: async (task, { recalculateDownstream }) => {
                            await dispatch(
                                DeleteTask({
                                    id: task.id,
                                    recalculateDownstream,
                                }),
                            );
                            setSelectedRow(null);
                        },
                        setPreviewTaskId,
                    }).catch(error => console.error('Error deleting task:', error));
                }}>
                <span ref={deleteModalRef} style={{ display: 'none' }}></span>
            </DoubleConfirmationModal>

            <SubtaskParentWarningModal
                open={Boolean(pendingSubtaskAdd)}
                onClose={handleSubtaskParentWarningClose}
                onConfirm={handleSubtaskParentWarningConfirm}
                confirmLoading={subtaskAddLoading}
            />

            <RecalculateScheduleModal
                open={Boolean(pendingScheduleChange)}
                onClose={() => !scheduleChangeLoading && setPendingScheduleChange(null)}
                onUpdateOnly={() => applyScheduleChange({ updateOnly: true })}
                onRecalculateDownstream={({ adjustDeadlines } = {}) =>
                    applyScheduleChange({ updateOnly: false, adjustDownstreamDeadlines: Boolean(adjustDeadlines) })
                }
                fieldLabel={pendingScheduleChange?.fieldLabel || 'this task'}
                affectedCount={pendingScheduleChange?.affectedCount}
                confirmLoading={scheduleChangeLoading}
            />

            <AdjustDeadlineModal
                open={Boolean(pendingDeadlineChange)}
                onClose={() => !deadlineChangeLoading && setPendingDeadlineChange(null)}
                onChangeDeadlineOnly={() => applyDeadlineChange({ adjustTimelineToDeadline: false })}
                onAdjustTimelineToDeadline={() => applyDeadlineChange({ adjustTimelineToDeadline: true })}
                canAdjustTimeline={pendingDeadlineChange?.canAdjustTimeline !== false}
                confirmLoading={deadlineChangeLoading}
            />

            <PauseTaskConfirmModal
                open={Boolean(pendingStatusChange)}
                onClose={() => !statusChangeLoading && setPendingStatusChange(null)}
                onThisOnly={handleStatusThisOnly}
                onWithFollowing={handleStatusWithFollowing}
                confirmLoading={statusChangeLoading}
                action={pendingStatusChange?.action || 'pause'}
            />
        </>
    );
};

export default TasksTable;
